import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const TAX_TYPES = [
  { value: 'brand', label: 'Brands' },
  { value: 'category', label: 'Categories' },
  { value: 'subcategory', label: 'Subcategories' },
  { value: 'deity', label: 'Deities' },
  { value: 'region', label: 'Regions' },
  { value: 'occasion', label: 'Occasions' },
];

export default function TaxonomyPage() {
  const [activeTab, setActiveTab] = useState('brand');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formValue, setFormValue] = useState('');
  const [formParent, setFormParent] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/taxonomy?type=${activeTab}`);
      setItems(data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDialog = (item = null) => {
    setEditItem(item);
    setFormValue(item?.value || '');
    setFormParent(item?.parent || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formValue.trim()) { toast.error('Value is required'); return; }
    try {
      const payload = { type: activeTab, value: formValue.trim(), parent: formParent || null, status: 'active' };
      if (editItem) {
        await api.put(`/taxonomy/${editItem.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/taxonomy', payload);
        toast.success('Added');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.value}"?`)) return;
    try {
      await api.delete(`/taxonomy/${item.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="taxonomy-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Master Data</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Manage taxonomy values for products</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="add-taxonomy-btn">
          <Plus size={16} className="mr-2" />Add {TAX_TYPES.find(t => t.value === activeTab)?.label?.slice(0, -1) || 'Item'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent border-b border-[#E5E0D8] rounded-none w-full justify-start gap-0 h-auto p-0" data-testid="taxonomy-tabs">
          {TAX_TYPES.map(t => (
            <TabsTrigger key={t.value} value={t.value}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#C5A059] data-[state=active]:text-[#C5A059] data-[state=active]:shadow-none text-sm px-4 py-3 text-[#5C5C5C]"
              data-testid={`tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TAX_TYPES.map(t => (
          <TabsContent key={t.value} value={t.value} className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner-gold" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E5E0D8] rounded-sm">
                <p className="text-[#5C5C5C]">No {t.label.toLowerCase()} added yet</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
                <table className="w-full" data-testid={`taxonomy-table-${t.value}`}>
                  <thead>
                    <tr className="border-b border-[#E5E0D8]">
                      <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Value</th>
                      {activeTab === 'subcategory' && <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Parent</th>}
                      <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Status</th>
                      <th className="text-right py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id} className="border-b border-[#E5E0D8] hover:bg-[#F5F2EB] transition-colors" data-testid={`taxonomy-row-${item.id}`}>
                        <td className="py-3 px-6 text-sm text-[#1A1A1A] font-medium">{item.value}</td>
                        {activeTab === 'subcategory' && <td className="py-3 px-6 text-sm text-[#5C5C5C]">{item.parent || '-'}</td>}
                        <td className="py-3 px-6 text-xs text-[#5C5C5C] capitalize">{item.status}</td>
                        <td className="py-3 px-6 text-right">
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => openDialog(item)} className="text-[#5C5C5C] hover:text-[#C5A059] p-1" data-testid={`edit-taxonomy-${item.id}`}><Pencil size={14} /></button>
                            <button onClick={() => handleDelete(item)} className="text-[#5C5C5C] hover:text-[#9E2A2B] p-1" data-testid={`delete-taxonomy-${item.id}`}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">{editItem ? 'Edit' : 'Add'} {TAX_TYPES.find(t => t.value === activeTab)?.label?.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Value</Label>
              <Input value={formValue} onChange={e => setFormValue(e.target.value)} className="rounded-sm border-[#E5E0D8]" data-testid="taxonomy-value-input" autoFocus />
            </div>
            {activeTab === 'subcategory' && (
              <div>
                <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Parent Category</Label>
                <Input value={formParent} onChange={e => setFormParent(e.target.value)} className="rounded-sm border-[#E5E0D8]" placeholder="Parent category name" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
            <Button onClick={handleSave} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="save-taxonomy-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
